import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, User, Globe, Briefcase, Users, Loader, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import logoIcon from '../assets/logo3.png';

const industries = ['Finance', 'Healthcare', 'Technology', 'Manufacturing', 'Other'];
const companySizes = ['1–10', '11–50', '51–200', '201–500', '500+'];

function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
    invite_code: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = (fields) => {
    const newErrors = {};
    fields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm(['email', 'password'])) return;

    setIsLoading(true);
    try {
      const { email, password } = formData;
      await login(email, password);
      // Redirect to the page they tried to visit or dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm(['email', 'password', 'name', 'company', 'invite_code'])) return;

    setIsLoading(true);
    try {
      await register(formData);
      setRegistrationSuccess(true);
      // Clear form data
      setFormData({
        email: '',
        password: '',
        name: '',
        company: '',
        invite_code: ''
      });
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-background-light flex flex-col items-center justify-center p-4">
      {/* Logo and Text */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 text-center"
      >
        <div className="flex items-center justify-center gap-3 mb-4">
          <img src={logoIcon} alt="Quantifaid Logo" className="h-12 w-12" />
          <span className="text-3xl tracking-wider font-bold">Quantifaid</span>
        </div>
        <p className="text-text-secondary text-lg">
          Measure, benchmark and improve your AI systems
        </p>
      </motion.div>

      {/* Auth Card */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md bg-background/80 backdrop-blur-sm rounded-xl border border-primary/20 p-8 shadow-xl"
      >
        {/* Tabs */}
        <div className="flex mb-6 border-b border-primary/20">
          <button
            className={`flex-1 py-2 px-4 text-center font-medium transition-colors ${
              activeTab === 'login'
                ? 'border-b-2 border-primary text-primary'
                : 'text-text-secondary hover:text-text'
            }`}
            onClick={() => {
              setActiveTab('login');
              setRegistrationSuccess(false);
            }}
          >
            Login
          </button>
          <button
            className={`flex-1 py-2 px-4 text-center font-medium transition-colors ${
              activeTab === 'register'
                ? 'border-b-2 border-primary text-primary'
                : 'text-text-secondary hover:text-text'
            }`}
            onClick={() => {
              setActiveTab('register');
              setRegistrationSuccess(false);
            }}
          >
            Register
          </button>
        </div>

        {/* Error Message */}
        {apiError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-error/10 text-error rounded-lg text-sm"
          >
            {apiError}
          </motion.div>
        )}

        {/* Registration Success Message */}
        {registrationSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-success/10 text-success rounded-lg text-sm flex items-center gap-2"
          >
            <CheckCircle size={20} />
            <span>Registration successful! Please login with your credentials.</span>
          </motion.div>
        )}

        {/* Login Form */}
        <AnimatePresence mode="wait">
          {activeTab === 'login' && (
            <motion.form
              key="login"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              onSubmit={handleLogin}
              className="space-y-4"
            >
              <h2 className="text-2xl font-bold text-text mb-4">Welcome back</h2>
              
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-background-light border border-primary/20 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {errors.email && <p className="text-error text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <input
                  type="password"
                  name="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full bg-background-light border border-primary/20 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {errors.password && <p className="text-error text-sm mt-1">{errors.password}</p>}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-background px-4 py-2 rounded-lg hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Logging in...</span>
                  </>
                ) : (
                  'Login'
                )}
              </button>

              <p className="text-center text-text-secondary">
                Don't have an account?{' '}
                <button
                  type="button"
                  className="text-primary hover:text-primary-light"
                  onClick={() => setActiveTab('register')}
                >
                  Register
                </button>
              </p>
            </motion.form>
          )}

          {/* Register Form */}
          {activeTab === 'register' && (
            <motion.form
              key="register"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              onSubmit={handleRegister}
              className="space-y-4"
            >
              <h2 className="text-2xl font-bold text-text mb-4">Create your account</h2>

              <div>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" size={20} />
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full bg-background-light border border-primary/20 rounded-lg pl-10 pr-4 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                {errors.name && <p className="text-error text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" size={20} />
                  <input
                    type="text"
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full bg-background-light border border-primary/20 rounded-lg pl-10 pr-4 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                {errors.company && <p className="text-error text-sm mt-1">{errors.company}</p>}
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-background-light border border-primary/20 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {errors.email && <p className="text-error text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <input
                  type="password"
                  name="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full bg-background-light border border-primary/20 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {errors.password && <p className="text-error text-sm mt-1">{errors.password}</p>}
              </div>

              <div>
                <input
                  type="text"
                  name="invite_code"
                  placeholder="Invite Code"
                  value={formData.invite_code}
                  onChange={handleInputChange}
                  className="w-full bg-background-light border border-primary/20 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {errors.invite_code && <p className="text-error text-sm mt-1">{errors.invite_code}</p>}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-background px-4 py-2 rounded-lg hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Creating account...</span>
                  </>
                ) : (
                  'Register'
                )}
              </button>

              <p className="text-center text-text-secondary">
                Already have an account?{' '}
                <button
                  type="button"
                  className="text-primary hover:text-primary-light"
                  onClick={() => setActiveTab('login')}
                >
                  Login
                </button>
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

export default Auth; 